import "./App.css";
import "./initKeycloak";

import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
// import { useEffect } from "react";
import { captureAttribution } from "./utils/attribution";
import { useStore } from "./stores/store";
import Header from "./layout-components/Header";
import SideBar from "./layout-components/SideBar";
import DashboardBuilder from "./pages/DashboardBuilder";
import ProtectedComponent from "./utils/ProtectedComponent";
import InitializeEvents from "./utils/initializeEvents";
import Login from "./pages/Login";
import NewsquawkResearch from "./widget-components/newsquawk-research/NewsquawkResearch";

// Pages
import WishlistAndFilterBuilder from "./pages/PortfolioBuilder/page";
import EconomicCalendar from "./pages/EconomicCalendar";
import Settings from "./pages/settings/Settings";
import HeadlinesPage from "./pages/HeadlinesPage";
import StocksPage from "./pages/StocksPage";

import TawkWrapper from "./utils/TawkWrapper";
import TaostManager from "./layout-components/ToastManager";
import useInitFirehoseStore from "./shared/api/firehose/useInitFirehoseStore";
import { useKeycloakTokenHandler } from "./hooks/useKeycloakTokenHandler";
import { useVersionCheck } from "./hooks/useVersionCheck";
import useAuth from "./hooks/useAuth";
import PopoutWidgets from "./ui-components/popout-widgets";
import { Button } from "./ui-components/button-with-loading";
import { useFontSize } from "./hooks/useFontSize";
import { useTheme } from "./hooks/useTheme";
import { SquawkPlayerProvider } from "./layout-components/squawk-player/SquawkPlayerContext";
import { SubscriptionBanner } from "./layout-components/SubscriptionBanner";
import { MarketsClosedBanner } from "./layout-components/MarketsClosedBanner";
import { DensityProvider } from "./contexts/DensityContext";
import { ConfirmProvider } from "./ui-components/ConfirmDialog";
import { usePageTracking } from "./hooks/usePageTracking";
import { useUserIdentification } from "./hooks/useUserIdentification";

function PageTracker() {
  usePageTracking();
  return null;
}

function UserIdentifier() {
  useUserIdentification();
  return null;
}

// Capture UTM params + referrer at module level so they're stored in sessionStorage
// before any user-initiated login redirect can change the URL.
captureAttribution();

function App() {
  const { keycloakInstance, keycloakInitFailed } = useAuth();

  const desktopSidebarOpen = useStore((state) => state.desktopSidebarOpen);

  /** Fetches news feed data inits firehose store
   * Subscribes to SSE /stream endpoint,
   * - on new data received, it adds the story to the store
   */
  useInitFirehoseStore();

  useKeycloakTokenHandler();

  // Poll /version.json to detect new deploys; prompts user to reload (or
  // silently reloads after the tab's been idle for 5+ min).
  useVersionCheck();

  // Apply user's font size preference to root HTML element
  useFontSize();

  // Apply user's theme preference to root HTML element
  useTheme();

  // Auto-trigger login when ?login=true is in URL (from external marketing site)
  // Disabled: was causing immediate redirect to Keycloak when clicking login
  // links from the marketing site, bypassing the custom Login page.
  // useEffect(() => {
  //   const params = new URLSearchParams(window.location.search);
  //   if (
  //     params.get("login") === "true" &&
  //     keycloakInstance &&
  //     !keycloakInstance.authenticated
  //   ) {
  //     window.history.replaceState({}, "", window.location.pathname);
  //     keycloakInstance.login();
  //   }
  // }, [keycloakInstance]);

  const reloadApp = () => {
    window.location.reload();
  };

  if (!keycloakInstance && !keycloakInitFailed) {
    return (
      <div className="animate-pulse flex h-screen items-center justify-center text-center text-gray-300">
        Verifying Credentials...
      </div>
    );
  }

  if (keycloakInitFailed) {
    return (
      <div className="flex flex-col h-screen items-center justify-center text-center text-gray-300 gap-6">
        <h3 className="text-2xl font-semibold">
          Sign-in temporarily unavailable
        </h3>
        <Button size="md" onClick={reloadApp}>
          Reload App
        </Button>
        <p className="max-w-screen-md mx-auto text-sm text-gray-400">
          We couldn&apos;t reach our sign-in service to start your session. This
          is usually a brief network hiccup or a blocked request, VPN,
          ad-blocker, or corporate proxy. Your place is saved, check your
          connection and try again. If it keeps failing, please contact support
          or try a different network.
        </p>
      </div>
    );
  }

  return (
    <>
      <InitializeEvents />
      <DensityProvider>
        <ConfirmProvider>
        <SquawkPlayerProvider>
          <Router>
            <UserIdentifier />
            <PageTracker />
            {/* Layout container */}
            <div
              id="app-layout"
              className="flex flex-col min-h-screen bg-gray-100 dark:bg-gray-950 transition-colors"
            >
              {/* Header */}
              <Header />
              {/* Subscription Banner */}
              <SubscriptionBanner />
              {/* Markets Closed Banner */}
              <MarketsClosedBanner />
              {/* Main content area */}
              <div className="flex flex-1">
                <SideBar />
                {/* Content Area */}
                <div
                  id="app-content"
                  className={`flex-1 min-w-0 max-w-full bg-gray-100 dark:bg-gray-950 transition-colors ${
                    desktopSidebarOpen ? "lg:ml-20" : ""
                  }`}
                >
                  <Routes>
                    <Route path="/" element={<DashboardBuilder />} />

                    <Route path="/dashboard" element={<DashboardBuilder />} />

                    <Route path="/login" element={<Login />} />

                    <Route path="/research" element={<NewsquawkResearch />} />
                    <Route path="/calendar" element={<EconomicCalendar />} />
                    <Route
                      path="/watchlists-filters"
                      element={<WishlistAndFilterBuilder />}
                    />

                    <Route path="/headlines" element={<HeadlinesPage />} />

                    <Route
                      path="/stocksnews"
                      element={
                        <ProtectedComponent
                          component={StocksPage}
                          fallback={Login}
                        />
                      }
                    />

                    <Route
                      path="/settings"
                      element={
                        <ProtectedComponent
                          component={Settings}
                          fallback={Login}
                        />
                      }
                    >
                      <Route path=":tab" element={<Settings />} />
                    </Route>
                  </Routes>
                  <ProtectedComponent
                    component={TawkWrapper}
                    fallback={() => null}
                  />
                </div>
              </div>
            </div>
          </Router>
        </SquawkPlayerProvider>
        </ConfirmProvider>
      </DensityProvider>

      <PopoutWidgets />
      <TaostManager />
    </>
  );
}

export default App;
